import { useState } from 'react';
import {
  useClinicalAlerts,
  useCreateClinicalAlert,
  useUpdateClinicalAlert,
  useDeleteClinicalAlert,
} from '../../services/httpServices/queries';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  AlertTriangle,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  AlertCircle,
} from 'lucide-react';

function toSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function ClinicalAlerts() {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [form, setForm] = useState({
    name: '',
    label: '',
    description: '',
    isActive: true,
  });

  const { data: alertsData, isLoading, error } = useClinicalAlerts();
  const createAlert = useCreateClinicalAlert();
  const updateAlert = useUpdateClinicalAlert();
  const deleteAlert = useDeleteClinicalAlert();

  const alerts = alertsData?.data || [];

  const resetForm = () => {
    setForm({ name: '', label: '', description: '', isActive: true });
    setShowForm(false);
    setEditing(null);
  };

  const startEdit = (a: any) => {
    setEditing(a);
    setForm({
      name: a.name,
      label: a.label,
      description: a.description || '',
      isActive: a.isActive,
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      name: form.name || toSlug(form.label),
    };
    if (editing) {
      updateAlert.mutate({ id: editing.id, data: payload }, { onSuccess: resetForm });
    } else {
      createAlert.mutate(payload, { onSuccess: resetForm });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteAlert.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Clinical Alerts</h1>
            <p className="text-sm text-text-muted">Alerts added here appear as checkboxes in the Patient Registration form.</p>
          </div>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Alert
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load clinical alerts</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {alerts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-text-muted">
                      No clinical alerts configured. Add your first alert to get started.
                    </td>
                  </tr>
                ) : (
                  alerts.map((alert: any) => (
                    <tr key={alert.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center">
                            <AlertTriangle size={14} className="text-warning" />
                          </div>
                          <p className="text-sm font-medium text-text">{alert.label}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted font-mono">{alert.name}</td>
                      <td className="px-4 py-3 text-sm text-text-muted max-w-xs truncate">{alert.description || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${alert.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                          {alert.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => startEdit(alert)} className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors">
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(alert)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alert Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">{editing ? 'Edit Clinical Alert' : 'New Clinical Alert'}</h2>
              <button onClick={resetForm} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Label *</label>
                <input
                  required
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. Drug Allergy"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Key (auto-generated if empty)</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. drug_allergy"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description of this alert"
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="caIsActive" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                <label htmlFor="caIsActive" className="text-sm text-text">Active</label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button type="button" onClick={resetForm} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">Cancel</button>
                <button type="submit" disabled={createAlert.isPending || updateAlert.isPending} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  {(createAlert.isPending || updateAlert.isPending) && <Loader2 size={16} className="animate-spin" />}
                  {editing ? 'Update Alert' : 'Create Alert'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete clinical alert?"
        description={
          <>
            This will remove <strong className="text-text">{deleteTarget?.label}</strong> from the
            patient registration form. This cannot be undone.
          </>
        }
        confirmLabel="Delete alert"
        loading={deleteAlert.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
